import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import {
    AggregatedMandateTerms,
    DealReceiverDetailMap,
    DebtProductRowData,
    DetailedDealTerms,
    DealTerms,
    Metric,
    ProductDealTerms,
    SecurityOfferedItem,
} from './debtProductTypes';
import { useAppSelector } from '../../app/hooks';
import {
    getDebtProductsDataAction,
    GET_SOURCES,
    generateProductMetrics,
    ProductTermsApiPayload,
    ProductTermsPayload,
    reorderProductLenders,
    restoreDebtProductsState,
} from '../../store/debtProducts/action';
import { selectDebtProductsData, selectProductsById, selectLendersById, getLenderKey } from '../../store/debtProducts/selectors';
import { USER_TYPE } from '../../utils/constants/user';
import { fetchConfigByConfigName, getAllTaskTrackerData } from '../../store/investor/investorApiHelper';
import { fetchAllPecData, getDealFormConfig, getDealsByStatusData } from '../../store/investor/apiHelpers';
import {
    getCompanyDealMetrics,
    getCompanyMetrics,
    getDebtProductsData,
    getMandateData,
} from '../../store/debtProducts/apiHelper_debtProducts';
import { Mixpanel } from '../../utils/mixpanel';
import {
    OPEN_EDIT_PROFILE,
    AICA_FILTERS_ON_DEBT_OPTIONS,
    AICA_MANDATE_SELECTION,
    AICA_SHARE_DEAL,
    AICA_UPDATE_MANDATE,
    CLICK_PROBABILITY_HELP,
    EXPAND_PRODUCT,
    VIEW_DEBT_PRODUCTS_PAGE,
    AICA_EDIT_TERMS,
} from '../../utils/constants/mixpanelEvents/investorEvents';
import { getTimeStamp } from '../../utils/dateUtils';
import {
    convertToFirstLetterCapital,
    deepCompare,
    getPlatformUserId,
    getSubdomain,
    hasNonZeroValue,
    isEmpty,
    isLenderType,
    toNumber,
} from '../../utils/utils';
import { useLocation, useSearchParams } from 'react-router-dom';
import { fetchCompanyUploadConfig, fetchUploadJourneyData, setDebtProductsFilters } from '../../store/company/action';
import { useDispatch } from 'react-redux';
import {
    createDeal,
    handleDealApplicationDrawer,
    setConfigTableModal,
    setDealStatusConfig,
    updateDeal,
    updateDealApplicationData,
    setDealDrawer,
    setTaskTrackerMap,
} from '../../store/investor/action';
import { fetchDataByConfig } from '../../common/ManageDealDrawer/ManageDealDrawerService';
import { polusBaseUrl } from '../../utils/axios-interceptor';
import { DATA_SOURCE_TYPE, DEAL_CREATOR_TYPE } from '../../common/ManageDealDrawer/DealDrawerConstants';
import { Button } from '../../common/_custom';
import Tooltip from '../../common/_custom/Tooltip/Tooltip';
import { plusWhite, ShareIcon, infoIconRed, infoIconGreen, greenCheck, errorIconRed } from '../../assets/hostedassets';
import { updateQueryParams } from '../../utils/searchParamUtils';
import { VISIBILITY_KEYS } from '../../utils/constants/visibilityKeys';
import { useFeatureVisibility } from '../../utils/useVisibilityConfig';
import { DISPATCH_EVENTS, MANDATE_STATES, QUALIFICATION_STATUS, VIEW_CONFIGS } from '../../utils/constants/commonConstants';
import { getMandateQualificationChecksData } from '../../store/investor/apiHelpers';
import { DealStatus } from '../../store/investor/reducer';
import styles from './DebtProducts.module.scss';
import {
    createDealTerms,
    getMinMax,
    removeProductFromLenderTerms,
    findProductRow,
    toggleLenderProduct,
    findLenderRow,
    getCalculatingKey,
} from './DebtProductsUtil';
import {
    DealPayloadScenario,
    ButtonText,
    EditableFieldConfig,
    SHARED_DEAL,
    EntityType,
    PRODUCT_TERMS_KEYS,
    PROBABILITY_TOOLTIP_MESSAGES,
    AUDITED_FINANCIAL_KEY_METRIC,
} from './DebtProductsConstants';
import useUIStateLogic from './useUIStateLogic';
import { DEAL_VIEW_TYPES, ENTITY_TYPES, FROM_DEBT_OPTIONS_PARAM } from '../../store/investor/constants';

const useDebtProductLogic = ({ params, selectedCompany }) => {
    // Lock body scroll on mount and restore on unmount; apply paddingRight to prevent layout shift
    useEffect(() => {
        const scrollBarWidth = window.innerWidth - document.documentElement.clientWidth;
        document.body.style.overflow = 'hidden';
        document.body.style.paddingRight = `${scrollBarWidth}px`;
        return () => {
            document.body.style.overflow = '';
            document.body.style.paddingRight = '';
        };
    }, []);

    /**
     * Generate tooltip content for probability score
     * @param probability - The probability value as a string
     * @param pecThreshold - Optional PEC threshold percentage as a string
     * @returns Tooltip content object or null if threshold is not present
     */
    const getProbabilityTooltip = (probability: string, pecThreshold?: string) => {
        // Don't show tooltip if threshold is not present
        if (!pecThreshold) {
            return null;
        }

        const probabilityValue = parseFloat(probability);
        const probabilityNum = Number.isNaN(probabilityValue) ? 0 : probabilityValue;

        const pecThresholdValue = parseFloat(pecThreshold);
        const pecThresholdNum = Number.isNaN(pecThresholdValue) ? 0 : pecThresholdValue;

        const isBelowThreshold = probabilityNum < pecThresholdNum;

        return {
            isBelowThreshold,
            message: isBelowThreshold ? PROBABILITY_TOOLTIP_MESSAGES.LOW_SCORE : PROBABILITY_TOOLTIP_MESSAGES.HIGH_SCORE,
            recommendedPercentage: isBelowThreshold ? `${pecThresholdNum}%` : null,
        };
    };
    const [loading, setLoading] = useState(true);
    const [searchParams, setSearchParams] = useSearchParams();
    const dispatch = useDispatch();
    const location = useLocation();
    const platformData = useAppSelector((state) => state.user.domainMetadata);
    const dealStatusConfig = useAppSelector((state) => state.investor.deals.dealStatusConfig);
    const [showDetailOverview, setShowDetailOverview] = useState<null | DebtProductRowData>(null);
    const {
        createDeal: createDealVisibility,
        qualificationChecks: qualificationChecksVisibility,
        mandateConfirmation: mandateConfirmationVisibility,
        mandateModal: mandateModalVisibility,
        dealShare: dealShareVisibility,
        editProfile: editProfileVisibility,
        accordion: accordionVisibility,
        selectProduct: selectProductVisibility,
        selectLender: selectLenderVisibility,
        getFeatureVisibility,
    } = useFeatureVisibility({
        createDeal: VISIBILITY_KEYS.MANDATE_CREATION.CREATE_DEAL,
        qualificationChecks: VISIBILITY_KEYS.QUALIFICATION_CHECKS.ENABLE,
        mandateConfirmation: VISIBILITY_KEYS.MANDATE_CREATION.CONFIRMATION_MODAL,
        mandateModal: VISIBILITY_KEYS.MANDATE_CREATION.MODAL,
        dealShare: VISIBILITY_KEYS.DEAL_SHARE.ENABLED,
        editProfile: VISIBILITY_KEYS.DEBT_PRODUCT.EDIT_PROFILE,
        accordion: VISIBILITY_KEYS.DEBT_PRODUCT.ACCORDION,
        selectProduct: VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_PRODUCT,
        selectLender: VISIBILITY_KEYS.DEBT_PRODUCT.SELECT_LENDER,
    });
    // Use memoized selector for denormalized data - prevents unnecessary re-renders
    const debtProductsData = useAppSelector(selectDebtProductsData);
    const productsById = useAppSelector(selectProductsById);
    const lendersById = useAppSelector(selectLendersById);
    const dealApplicationDrawerState = useAppSelector((state) => state.investor.dealApplicationDrawer);
    const [noPecExists, setNoPECExists] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [defaultFilters, setDefaultFilters] = useState({});
    const [companyMetricsDataExists, setCompanyMetricsDataExists] = useState(false);
    const [companyMetricsData, setCompanyMetricsData] = useState<any>({});
    const [metricsMetadata, setMetricsMetadata] = useState<{ userName?: string }>({});
    const [percentageCompletion, setPercentageCompletion] = useState<any>(undefined);
    const [configLoaded, setConfigLoaded] = useState(false);
    const [mandateState, setMandateState] = useState(null);
    const [dealPayload, setDealPayload] = useState<{
        products: string[];
        lenders: Map<string, string[]>;
    }>({
        products: [],
        lenders: new Map(),
    });
    const originalDealPayloadRef = useRef<{
        products: string[];
        lenders: Map<string, string[]>;
    }>({
        products: [],
        lenders: new Map(),
    });
    const originalProductTermsRef = useRef<DetailedDealTerms>({});
    const originalLenderTermsRef = useRef<DealReceiverDetailMap>({});
    // Ref to store original Redux debt products state for discarding changes
    const originalDebtProductsRef = useRef<{
        productsById: Record<string, any>;
        lendersById: Record<string, any>;
    }>({
        productsById: {},
        lendersById: {},
    });

    const dealPayloadRef = useRef<{
        dealPayload: {
            products: string[];
            lenders: Map<string, string[]>;
        };
        dealId: string;
        mandateState: string | null;
        dealStatus: string;
    }>({
        dealPayload: {
            products: [],
            lenders: new Map(),
        },
        dealId: '',
        mandateState: null,
        dealStatus: '',
    });

    const originalAggregatedTermsRef = useRef<AggregatedMandateTerms>({
        maximumDealAmount: 0,
        minimumRoi: 0,
        maximumRoi: 0,
        minimumTenure: 0,
        maximumTenure: 0,
        products: [],
        security: [],
    });

    const debtProductsFilters = useAppSelector((state) => state.company.debtProductsFilters);

    const isConfirmationModalVisible = mandateConfirmationVisibility.show;
    const dealDrawerState = useAppSelector((state) => state.investor.dealDrawer);
    const user = useAppSelector((state) => state.user);
    const dealProducts = useAppSelector((state) => state.investor.dealApplicationDrawer.allProducts);
    const dashboardConfig = useAppSelector((state) => state.dashboard.dashboardReducer.config);
    const taskTrackerMap = useAppSelector((state) => state.investor.taskTrackerMap);
    const loggedInUser = user?.userData;
    const isPartnerSide = loggedInUser?.accountType === USER_TYPE.PARTNER;
    const isCompanySide = loggedInUser?.accountType === USER_TYPE.COMPANY || location.pathname.includes('company');
    const isLender = isLenderType(loggedInUser?.accountType);
    const platformUserId = getPlatformUserId();
    const disableLastDealSharedClick = !params.id || isPartnerSide || isLenderType(loggedInUser?.accountType) || isCompanySide;
    const showEmptyScreen = noPecExists || !companyMetricsDataExists || (isPartnerSide && debtProductsData.rows.length === 0);
    const [savingDraft, setSavingDraft] = useState(false);
    const [draftDealsData, setDraftDealsData] = useState([]);
    const [selectedMandate, setSelectedMandate] = useState<any>(null);
    const [dealId, setDealId] = useState('');
    const [dealStatus, setDealStatus] = useState('');
    const [mandateQualificationChecksData, setMandateQualificationChecksData] = useState<any>({ result: false, rules: [] });
    const [productTerms, setProductTerms] = useState<DetailedDealTerms>({});
    const [lenderTerms, setLenderTerms] = useState<DealReceiverDetailMap>({});
    const [aggregatedMandateTerms, setAggregatedMandateTerms] = useState<AggregatedMandateTerms>({
        maximumDealAmount: 0,
        minimumRoi: 0,
        maximumRoi: 0,
        minimumTenure: 0,
        maximumTenure: 0,
        products: [],
        security: [],
    });

    const [dealTerms, setDealTerms] = useState<DealTerms>({
        EndUseofCapital: '',
        dealNotes: [],
        documents: [],
    });
    const [selectedUsers, setSelectedUsers] = useState<
        Record<
            string,
            {
                userIds: Array<{ name: string; userId: string }>;
                type: string;
            }
        >
    >({});

    const [isBannerDismissed, setIsBannerDismissed] = useState(false);
    const [hasUserClickedRow, setHasUserClickedRow] = useState(false);

    const showAuditedBanner = useCallback(() => {
        setHasUserClickedRow(true);
        setIsBannerDismissed(false);
    }, []);

    // Check if there are unsaved changes (for modal trigger)
    // Only compares productTerms and lenderTerms since these track actual user changes
    // (selections, edited amounts/ROI/tenure). Redux state changes via API, not user edits.
    const checkForUnsavedChanges = useCallback((): boolean => {
        if (!dealId) return false;

        const hasOriginalProductTerms = Object.keys(originalProductTermsRef.current).length > 0;
        const hasOriginalLenderTerms = Object.keys(originalLenderTermsRef.current).length > 0;
        const hasCurrentProductTerms = Object.keys(productTerms).length > 0;
        const hasCurrentLenderTerms = Object.keys(lenderTerms).length > 0;

        if (!hasOriginalProductTerms && !hasCurrentProductTerms && !hasOriginalLenderTerms && !hasCurrentLenderTerms) return false;

        const hasProductTermsChanges = !deepCompare(originalProductTermsRef.current, productTerms);
        const hasLenderTermsChanges = !deepCompare(originalLenderTermsRef.current, lenderTerms);

        return hasProductTermsChanges || hasLenderTermsChanges;
    }, [productTerms, lenderTerms, dealId]);

    // Use UI state hook for managing loading states, transitions, and animations
    const {
        isRowCalculating,
        addToCalculatingState,
        removeFromCalculatingState,
        clearCalculatingRows,
        isRowShimmering,
        addToShimmeringState,
        removeFromShimmeringState,
        clearShimmeringRows,
        isTransitioning,
        setIsTransitioning,
        showShareSuccessModal,
        setShowShareSuccessModal,
        calculatingRowIds,
        showSecurityModal,
        setShowSecurityModal,
        showUnsavedChangesModal,
        handleActionWithUnsavedCheck,
        handleCloseWithoutSaving,
        handleSaveChanges,
        handleCloseUnsavedModal,
        hasPendingAction,
        isMandateSharingDrawerOpen,
        setIsMandateSharingDrawerOpen,
        isShareWithLenderModalOpen,
        setIsShareWithLenderModalOpen,
        // Row hover state for editable fields
        hoveredEditableRow,
        setEditableRowHover,
        isEditableRowHovered,
        getCalculatingRowKey,
    } = useUIStateLogic({ checkForUnsavedChanges });

    const hasUnsavedChanges = () => {
        return !(deepCompare(originalProductTermsRef.current, productTerms) && deepCompare(originalLenderTermsRef.current, lenderTerms));
    };

    /**
     * Aggregates mandate-related values across all selected product terms.
     *
     * This function computes:
     * - The total maximum deal amount (sum of maximumDealAmount)
     * - The minimum and maximum ROI (via getMinMax on maximumRoi)
     * - The minimum and maximum tenure (via getMinMax on minimumTenure)
     * - A unique list of all security types offered
     * - A list of product names for all selected productIds
     *
     * All min/max calculations consider negative values as well.
     *
     *
     * @returns {{
     *   maximumDealAmount: number,
     *   minimumRoi: number,
     *   maximumRoi: number,
     *   minimumTenure: number,
     *   maximumTenure: number,
     *   products: string[],
     *   security: string[]
     * }}
     */

    const aggregateMandateTerms = useMemo(() => {
        const selectedProductIds = Object.keys(productTerms);

        if (selectedProductIds.length === 0) {
            return {
                maximumDealAmount: 0,
                minimumRoi: 0,
                maximumRoi: 0,
                minimumTenure: 0,
                maximumTenure: 0,
                products: [],
                security: [],
            };
        }

        // Get product rows from Redux that are in productTerms
        const selectedProductRows = debtProductsData.rows.filter((row) => selectedProductIds.includes(row.productId));

        const totalAmount = selectedProductRows.reduce((sum, row) => sum + +(row.amount || 0), 0);

        const { min: minRoi, max: maxRoi } = getMinMax(selectedProductRows, PRODUCT_TERMS_KEYS.ROI);

        const { min: minTenure, max: maxTenure } = getMinMax(selectedProductRows, PRODUCT_TERMS_KEYS.TENURE);

        const security = Array.from(
            new Set(selectedProductRows.flatMap((row) => (row.security || []).map((sec) => sec.type).filter(Boolean))),
        );

        const products = selectedProductRows.map((row) => row.productName).filter(Boolean) as string[];

        return {
            maximumDealAmount: totalAmount,
            minimumRoi: minRoi,
            maximumRoi: maxRoi,
            minimumTenure: minTenure,
            maximumTenure: maxTenure,
            products,
            security,
        };
    }, [productTerms, debtProductsData.rows]);

    // Update aggregatedMandateTerms based on whether user has deviated from original API state
    useEffect(() => {
        const hasChangesFromOriginal =
            !deepCompare(originalProductTermsRef.current, productTerms) || !deepCompare(originalLenderTermsRef.current, lenderTerms);

        if (hasChangesFromOriginal) setAggregatedMandateTerms(aggregateMandateTerms);
        else setAggregatedMandateTerms(originalAggregatedTermsRef.current);
    }, [aggregateMandateTerms, productTerms, lenderTerms]);

    // Functions to update dealTerms
    const updateEndUseOfCapital = (value: string) => {
        setDealTerms((prev) => ({
            ...prev,
            EndUseofCapital: value,
        }));
    };

    const addDealNote = useCallback(
        (note: string) => {
            setDealTerms((prev) => ({
                ...prev,
                dealNotes: [
                    {
                        note,
                        noteBy: user.userData?.userName,
                        noteDate: Date.now(),
                    },
                ],
            }));
        },
        [user.userData?.userName],
    );

    const updateDealDocuments = useCallback((documents: Array<{ documentName: string; docId: string | number; action: string }>) => {
        setDealTerms((prev) => ({
            ...prev,
            documents,
        }));
    }, []);

    const updateSelectedUsers = (dealReceiverId: string, users: Array<{ name: string; userId: string }>) => {
        setSelectedUsers((prev) => ({
            ...prev,
            [dealReceiverId]: {
                userIds: users,
                type: 'SELECTED',
            },
        }));
    };

    // Mandate statuses that can be visible in the mandate dropdown
    const validMandateDropdownStatuses = useMemo(() => {
        return (
            (dealStatusConfig?.mandate as any[])
                ?.filter((item: any) => item.mandateDropdownVisibility === true)
                ?.map((item: any) => item.value) || []
        );
    }, [dealStatusConfig]);

    const tooltipContent = useMemo(() => {
        if (!mandateQualificationChecksData?.rules?.length) return null;

        return (
            <div style={{ minWidth: '300px' }}>
                {mandateQualificationChecksData?.rules?.map((rule: any, index: number) => (
                    <div
                        key={index}
                        style={{
                            display: 'flex',
                            alignItems: 'flex-start',
                            gap: '0.75rem',
                        }}
                    >
                        <div className={styles.RulesMessage}>
                            <img
                                src={rule.result === true ? greenCheck : errorIconRed}
                                alt={rule.result === true ? QUALIFICATION_STATUS.QUALIFIED : QUALIFICATION_STATUS.UNQUALIFIED}
                                style={{ height: '1rem', width: '1rem' }}
                            />
                        </div>
                        <span className={styles.RulesMessageText}>{rule.message}</span>
                    </div>
                ))}
            </div>
        );
    }, [mandateQualificationChecksData?.rules]);

    const pillStyles = useMemo(
        () => ({
            display: 'flex' as const,
            alignItems: 'center' as const,
            gap: '0.5rem',
            padding: '0.5rem',
            borderRadius: '1rem',
            fontSize: '0.75rem',
            fontFamily: "'Gilroy-Medium', sans-serif",
            fontWeight: 500,
            lineHeight: '1.25rem',
            backgroundColor: mandateQualificationChecksData?.result === false ? '#FFEEEE' : '#E1FFE1',
            color: mandateQualificationChecksData?.result === false ? '#DF666A' : '#49A71B',
            border: 'none',
            whiteSpace: 'nowrap' as const,
            cursor: mandateQualificationChecksData?.rules?.length > 0 ? 'pointer' : 'default',
            boxShadow: '0px 0px 8px 0.5px #00000026',
        }),
        [mandateQualificationChecksData?.rules, mandateQualificationChecksData?.result],
    );

    useEffect(() => {
        Mixpanel.track(VIEW_DEBT_PRODUCTS_PAGE, {
            user_email: user.userData.primaryEmail,
            timestamp: getTimeStamp(),
            company_id: selectedCompany?.assesseeOrgId,
            company_name: selectedCompany?.orgName,
            pno_id: user.userData.investorOrgId,
            pno_name: user.domainMetadata?.orgName,
        });

        if (params.id && (!taskTrackerMap || Object.keys(taskTrackerMap).length === 0)) {
            getAllTaskTrackerData(
                { orgId: params.id as string },
                {
                    onSuccess: (taskTrackerMapData) => {
                        dispatch(setTaskTrackerMap(taskTrackerMapData));
                    },
                    onError: (error) => {
                        console.error('Failed to fetch task tracker data:', error);
                    },
                },
            );
        }
    }, []);

    const getAllProducts = () => {
        const payload = {
            aggregatorId: loggedInUser?.investorOrgId,
        };
        fetchDataByConfig(payload, 'GET_ALL_PRODUCTS', 'GET', polusBaseUrl).then((response) => {
            if (response) {
                const res = response.data.responseBody.data;
                let option = Object.values(res)?.map((option: any) => {
                    return { label: option.productName, value: option.productId };
                });
                dispatch(updateDealApplicationData({ allProducts: option }));
            }
        });
    };

    useEffect(() => {
        if (!dealProducts) {
            getAllProducts();
        }
    }, []);

    // Ref to capture mandateId from navigation state (used for navigation from Add Lender action)
    // Using ref ensures we only read it once and consume it without triggering re-renders
    const initialMandateIdRef = useRef<string | null>((location.state as any)?.mandateId || null);
    // Track if initial mandate selection has been done (either from state or default)
    const hasInitializedMandateRef = useRef(false);

    // Fetches MANDATE deals data
    useEffect(() => {
        getDealsByStatusData(
            { orgId: params.id as string, status: validMandateDropdownStatuses },
            {
                onSuccess: (data) => {
                    if (isEmpty(data) && !isEmpty(draftDealsData)) return;

                    setDraftDealsData(data);

                    // Skip mandate selection if already initialized (prevents resetting on re-fetches)
                    if (hasInitializedMandateRef.current) {
                        return;
                    }

                    hasInitializedMandateRef.current = true;

                    // Check if mandateId was provided via navigation state
                    const mandateIdFromState = initialMandateIdRef.current;
                    if (mandateIdFromState && data && data.length > 0) {
                        // Find and select the mandate matching the navigation state
                        const matchingMandate = data.find((mandate: any) => mandate.id === mandateIdFromState);
                        if (matchingMandate) {
                            setSelectedMandate(matchingMandate);
                            setDealId(matchingMandate.id);
                        } else {
                            // Fallback to first mandate if no match found
                            setSelectedMandate(data[0]);
                            setDealId(data[0].id);
                        }
                        // Clear the navigation state to prevent re-use on refresh
                        window.history.replaceState({}, '');
                    } else if (data && data.length > 0) {
                        // Set first mandate as selected and its dealId
                        setSelectedMandate(data[0]);
                        setDealId(data[0].id);
                    } else {
                        // No mandates exist - fetch products without mandate
                        setSelectedMandate(null);
                        setDealId('');
                        // Fetch products without mandate if config is loaded
                        if (configLoaded) fetchPECDataAndDebtProductsData(isEmpty(defaultFilters) ? null : defaultFilters);
                    }
                },
            },
        );
    }, [params.id, validMandateDropdownStatuses]);

    useEffect(() => {
        dealPayloadRef.current = {
            dealPayload,
            dealId,
            mandateState,
            dealStatus,
        };
    }, [dealPayload, dealId, mandateState, dealStatus]);

    useEffect(() => {
        if (selectedMandate && !dealDrawerState.open) {
            updateDealStatusOnMandateQualification(selectedMandate?.id || '');
        } else {
            setMandateQualificationChecksData({ result: false, rules: [] });
        }
    }, [dealDrawerState.open]);

    useEffect(() => {
        const handleRequirementUpdated = () => {
            const currentData = dealPayloadRef.current;

            if (!currentData.dealId) {
                return;
            }

            updateDealStatusOnMandateQualification(currentData.dealId, {
                mandateState: currentData.mandateState,
                dealStatus: currentData.dealStatus,
            });
        };

        window.addEventListener(DISPATCH_EVENTS.REQUIREMENT_UPDATED, handleRequirementUpdated);

        return () => {
            window.removeEventListener(DISPATCH_EVENTS.REQUIREMENT_UPDATED, handleRequirementUpdated);
        };
    }, []);

    // Fetches deal status config for mandate deals' statuses
    useEffect(() => {
        getDealFormConfig(
            {
                accountType: user.userData.accountType,
                investorOrgId: user.userData.investorOrgId,
                primaryEmail: user.userData.primaryEmail,
                configName: VIEW_CONFIGS.AICA_DEAL_NAVBAR_VIEW,
            },
            {
                onSuccess: (data) => {
                    dispatch(setDealStatusConfig(data?.dealStatusDropdownOptions || {}));
                },
            },
        );
    }, [user.userData.accountType, user.userData.investorOrgId, user.userData.primaryEmail]);

    const fetchMandateDeal = () => {
        if (isEmpty(dealId)) {
            const emptyTerms = {
                maximumDealAmount: 0,
                minimumRoi: 0,
                maximumRoi: 0,
                minimumTenure: 0,
                maximumTenure: 0,
                products: [],
                security: [],
            };
            setProductTerms({});
            setLenderTerms({});
            setAggregatedMandateTerms(emptyTerms);
            originalAggregatedTermsRef.current = emptyTerms;
            setDealStatus('');
            setMandateState(null);
            return;
        }

        getMandateData(
            {
                requestPayload: {
                    dealId: dealId,
                    aggregated: true,
                },
            },
            {
                onSuccess: (res) => {
                    const apiData = res?.data || res;

                    const convertedProductTerms: DetailedDealTerms = {};
                    if (apiData?.detailedDealTerms) {
                        Object.keys(apiData.detailedDealTerms).forEach((productId) => {
                            const productData = apiData.detailedDealTerms[productId];
                            convertedProductTerms[productId] = {
                                ...productData.terms,
                                isShared: productData.isShared ?? 0,
                            };
                        });
                    }

                    setProductTerms(convertedProductTerms);
                    originalProductTermsRef.current = JSON.parse(JSON.stringify(convertedProductTerms));

                    const convertedLenderTerms: DealReceiverDetailMap = {};
                    if (apiData?.dealReceiverDetailMap) {
                        Object.keys(apiData.dealReceiverDetailMap).forEach((lenderId) => {
                            const lenderData = apiData.dealReceiverDetailMap[lenderId];
                            convertedLenderTerms[lenderId] = {};
                            Object.keys(lenderData).forEach((productId) => {
                                const productData = lenderData[productId];
                                convertedLenderTerms[lenderId][productId] = {
                                    ...productData.terms,
                                    isShared: productData.isShared ?? 0,
                                };
                            });
                        });
                    }
                    setLenderTerms(convertedLenderTerms);
                    originalLenderTermsRef.current = JSON.parse(JSON.stringify(convertedLenderTerms));

                    // Set mandateState and dealId from response
                    setMandateState(apiData?.mandateState);
                    setDealId(apiData?.id);
                    // Set aggregatedMandateTerms from API on initial load
                    const apiAggregatedTerms = apiData?.aggregatedDealTerms || {};
                    // change the key name from securityOffered to security
                    apiAggregatedTerms.security = apiAggregatedTerms.securityOffered || [];
                    delete apiAggregatedTerms.securityOffered;
                    setAggregatedMandateTerms(apiAggregatedTerms);
                    originalAggregatedTermsRef.current = apiAggregatedTerms;

                    if (!isEmpty(apiData.dealTerms) && apiData.dealStatus !== 'DRAFT_DELETED') {
                        setCompanyMetricsData({ dealTerms: apiData.dealTerms });
                    }

                    updateDealStatusOnMandateQualification(apiData?.id, apiData);

                    setDealStatus(apiData?.dealStatus);
                },
                onError: () => {
                    const emptyTerms = {
                        maximumDealAmount: 0,
                        minimumRoi: 0,
                        maximumRoi: 0,
                        minimumTenure: 0,
                        maximumTenure: 0,
                        products: [],
                        security: [],
                    };
                    setProductTerms({});
                    setLenderTerms({});
                    setAggregatedMandateTerms(emptyTerms);
                    originalAggregatedTermsRef.current = emptyTerms;
                    setDealStatus('');
                    setMandateState(null);
                    setDealId('');
                },
            },
        );
    };

    /**
     * Handles mandate selection from the dropdown
     *
     * @param mandate - Selected mandate object or null for clearing selection
     *
     * Behavior:
     * - Sets selected mandate and dealId when a mandate is selected
     * - Clears selection when cross button is clicked (mandate is null)
     * - Fetches latest metrics data when clearing selection to allow creating new mandate
     */
    const registerMixpanelForMandateSelection = (mandate: any) => {
        Mixpanel.track(AICA_MANDATE_SELECTION, {
            user_name: user?.userData?.userName,
            user_email: user?.userData?.primaryEmail,
            current_url: window.location.href,
            company_name: selectedCompany?.orgName,
            user_role: user?.userData?.accountType,
            mandate_id: mandate?.id || '',
            org_id: selectedCompany?.assesseeOrgId,
        });
    };
    const handleMandateSelection = (mandate: any) => {
        // Track mandate selection event
        registerMixpanelForMandateSelection(mandate);
        setSelectedMandate(mandate);
        setDealId(mandate?.id || '');
        dispatch(setDebtProductsFilters(defaultFilters));
        fetchCompanyMetrics(false);
        updateQueryParams(searchParams, setSearchParams, { filters: true }, location);
        // Fetch latest metrics when clearing selection for new mandate creation
        if (isEmpty(mandate)) {
            fetchCompanyMetrics(true);
        }
    };

    // Auto-call fetchMandateDeal when dealId changes
    useEffect(() => {
        fetchMandateDeal();
        if (dealId) fetchDebtProductsData(undefined, dealId, true);
    }, [dealId]);

    // To handle Debt Products page on company side to avoid Unauthorized error
    useEffect(() => {
        if (isCompanySide) {
            dispatch(
                fetchUploadJourneyData({
                    accountType: user.userData.accountType,
                    assesseeOrgId: params.id as string,
                }),
            );

            dispatch(
                fetchCompanyUploadConfig({
                    accountType: user.userData.accountType,
                    primaryEmail: user.userData.primaryEmail ?? user.userData.investorPrimaryEmail,
                    investorOrgId: user.userData.investorOrgId,
                    configName: 'AICA_BORROWER_ONBOARDING_DATA_BY_BORROWER',
                    assesseeOrgId: params.id as string,
                }),
            );
        }
    }, [user.userData.accountType, user.userData.primaryEmail, user.userData.investorPrimaryEmail]);

    useEffect(() => {
        if (!dealApplicationDrawerState.open && mandateState !== MANDATE_STATES.SHARED) {
            fetchMandateDeal();
        }
    }, [dealApplicationDrawerState.open]);

    useEffect(() => {
        if (searchParams.get('filters') !== 'true') {
            dispatch(setDebtProductsFilters({}));
        }
    }, [searchParams.get('filters')]);

    useEffect(() => {
        if (user.userData.investorOrgId && dashboardConfig?.additionalDetailsConfig && !configLoaded) {
            fetchHeadersFromConfig();
            const isEditProfile = dealDrawerState?.drawerLabel === 'Edit Profile';
            fetchCompanyMetrics(false, isEditProfile);
        }
    }, [user.userData.investorOrgId, dashboardConfig?.additionalDetailsConfig, configLoaded, searchParams.get('draftDeleteUpdated')]);

    useEffect(() => {
        if (configLoaded && hasInitializedMandateRef.current && !dealId) {
            fetchPECDataAndDebtProductsData(isEmpty(defaultFilters) ? null : defaultFilters);
        }
    }, [configLoaded, dealId, hasInitializedMandateRef.current]);

    /**
     * Normalizes raw API data into the normalized state structure
     * Used to store original state for discard changes functionality
     */
    const normalizeDebtProductsData = (rows: DebtProductRowData[]) => {
        const normalizedProductsById: Record<string, any> = {};
        const normalizedLendersById: Record<string, any> = {};

        rows.forEach((row) => {
            const { lenders, ...productData } = row;
            const productId = row.productId;

            // Create lender IDs array and normalize lenders
            const lenderIds: string[] = [];
            if (lenders && Array.isArray(lenders)) {
                lenders.forEach((lender) => {
                    if (lender.lenderId) {
                        const lenderKey = getLenderKey(lender.lenderId, productId);
                        lenderIds.push(lenderKey);
                        normalizedLendersById[lenderKey] = {
                            ...lender,
                            parentProductId: productId,
                        };
                    }
                });
            }

            // Store normalized product
            normalizedProductsById[productId] = {
                ...productData,
                lenderIds,
            };
        });

        return { productsById: normalizedProductsById, lendersById: normalizedLendersById };
    };

    const fetchHeadersFromConfig = () => {
        fetchConfigByConfigName({
            accountType: user.userData.accountType,
            investorOrgId: user.userData.investorOrgId,
            primaryEmail: user.userData.primaryEmail,
            assesseeOrgId: params.id as string,
            userRole: user.userData?.roles?.[0],
            configName: VIEW_CONFIGS.AICA_DEBT_PRODUCT,
        })
            .then((configResponse) => {
                let headers = [{ id: 'caret', label: '-' }, ...(configResponse?.additionalDetailsConfig?.columns || [])];
                // add element in the second place
                headers.splice(1, 0, { id: 'select', label: '' });
                // Place "Last Shared" column before timeline; if no timeline in config, append at end
                const timelineIndex = headers.findIndex((h: { id: string }) => h.id === 'timeline');
                if (timelineIndex !== -1) {
                    headers.splice(timelineIndex, 0, { id: 'latestSharedDealDate', label: 'Last Shared' });
                } else {
                    headers.push({ id: 'latestSharedDealDate', label: 'Last Shared' });
                }
                //Take out the Debt Products name title from dashboard config
                const debtProductName = dashboardConfig?.additionalDetailsConfig?.companyTabs?.find((tab) => tab.id === 'debtProducts')
                    ?.title;
                const showFilters = configResponse?.additionalDetailsConfig?.filters?.showFilters;
                const visibilityStates = {
                    ...configResponse?.additionalDetailsConfig?.visibilityStates,
                    debtProductName: debtProductName,
                };
                dispatch(
                    getDebtProductsDataAction(GET_SOURCES.GET_HEADERS, {
                        headers: headers,
                        visibilityStates: visibilityStates,
                    }),
                );
                setShowFilters(showFilters);
                let updatedFilters: Record<string, Record<string, number>> = {};
                const filterPages = configResponse.additionalDetailsConfig.filters.pages || [];

                if (showFilters) {
                    filterPages.forEach((page) => {
                        page.config.forEach((section) => {
                            // Ensure the sectionKey exists in updatedFilters as an object
                            if (!updatedFilters[section.sectionKey]) {
                                updatedFilters[section.sectionKey] = {};
                            }

                            Object.entries(section.config).forEach(([key, value]: [string, any]) => {
                                if (value.minimumValue !== undefined && value.maximumValue !== undefined) {
                                    const capitalizedKey = convertToFirstLetterCapital(key);
                                    updatedFilters[section.sectionKey][`minimum${capitalizedKey}`] = value.minimumValue;
                                    updatedFilters[section.sectionKey][`maximum${capitalizedKey}`] = value.maximumValue;
                                }
                            });
                        });
                    });
                    updatedFilters.filters['filterType'] = configResponse?.additionalDetailsConfig?.filters.filterType;
                    setDefaultFilters(updatedFilters);
                } else {
                    setDefaultFilters({});
                }
                setConfigLoaded(true);
            })
            .catch(() => {
                setConfigLoaded(false);
            });
    };

    const fetchCompanyMetrics = async (isClearDeal: boolean, isEditProfile: boolean = false) => {
        let companyMetricsDataExists = false;
        let latestMetricsData: any = {};

        await Promise.all([
            getCompanyMetrics({
                assesseeOrgId: params.id as string,
            }).then((data) => {
                if (data) {
                    const metricsData = data.companyMetrics ?? {};
                    if (
                        !companyMetricsData?.dealTerms ||
                        !dealStatus ||
                        isClearDeal ||
                        isEditProfile ||
                        searchParams.has('draftDeleteUpdated')
                    ) {
                        setCompanyMetricsData(metricsData);
                        setMetricsMetadata({ userName: data.userName });
                        searchParams.delete('draftDeleteUpdated');
                        // updateQueryParams(searchParams, setSearchParams, {}, location);
                    }
                    if (Object.keys(metricsData || {}).length) {
                        companyMetricsDataExists = true;
                    }
                    latestMetricsData = metricsData;
                    setPercentageCompletion(data?.percentageCompletion);
                    setCompanyMetricsDataExists(true);
                }
            }),

            getCompanyDealMetrics({
                requestPayload: {
                    requestId: getTimeStamp(),
                    parentOrgId: user.userData.investorOrgId,
                    orgId: params.id as string,
                    type: 'auditedfinancials',
                    startDate: null,
                    endDate: null,
                    frequency: 'Yearly',
                    metricList: AUDITED_FINANCIAL_KEY_METRIC,
                    taskStepsList: ['AION_AUDITED_FINANCIAL'],
                    mis: 'Balance Sheet',
                    raw: true,
                    classificationId: 30,
                },
            }).then((data) => {}),
        ])
            .then(() => {})
            .catch((error) => {
                console.error('Error fetching company metrics:', error);
            });
        return latestMetricsData;
    };

    /**
     * Updates productTerms when a product is selected/deselected
     */
    const updateProductTerms = (productId: string) => {
        setProductTerms((prev) => {
            const exists = !!prev[productId];
            let updated = { ...prev };

            if (exists) {
                // Remove from productTerms
                delete updated[productId];

                // Remove from lenderTerms also
                setLenderTerms((prevLenderTerms) => removeProductFromLenderTerms(prevLenderTerms, productId));
            } else {
                // Add new product
                const row = findProductRow(debtProductsData.rows, productId);
                if (row) updated[productId] = createDealTerms(row);
            }

            return updated;
        });
    };

    /**
     * Updates lenderTerms when a lender is selected/deselected
     */
    const updateLenderTerms = (lenderId: string, productId: string) => {
        const row = findLenderRow(debtProductsData.rows, productId, lenderId);

        setLenderTerms((prev) => {
            const updated = toggleLenderProduct(prev, lenderId, productId, row);
            return updated;
        });

        // Ensure productTerms has the parent product
        setProductTerms((prev) => {
            if (prev[productId]) return prev;

            const productRow = findProductRow(debtProductsData.rows, productId);
            if (!productRow) return prev;

            return {
                ...prev,
                [productId]: createDealTerms(productRow),
            };
        });
    };

    const setPreviousDealData = () => {
        setProductTerms(originalProductTermsRef.current);
        setLenderTerms(originalLenderTermsRef.current);
        // Also restore Redux debt products state to original values
        if (
            Object.keys(originalDebtProductsRef.current.productsById).length > 0 ||
            Object.keys(originalDebtProductsRef.current.lendersById).length > 0
        ) {
            dispatch(restoreDebtProductsState(originalDebtProductsRef.current.productsById, originalDebtProductsRef.current.lendersById));
        }
    };

    const fetchMandateQualifications = (mandateId?: string): Promise<any> => {
        return new Promise((resolve) => {
            getMandateQualificationChecksData(
                {
                    orgId: params.id as string,
                    investorOrgId: user.userData.investorOrgId,
                    dealId: mandateId,
                },
                {
                    onSuccess: (data) => {
                        setMandateQualificationChecksData(data);
                        resolve(data);
                    },
                    onError: () => {
                        const errorData = { result: false, rules: [] };
                        setMandateQualificationChecksData(errorData);
                        resolve(errorData);
                    },
                },
            );
        });
    };

    const updateDealStatusOnMandateQualification = async (mandateId: string, selectedMandateData?: any) => {
        try {
            const selectedMandateState = selectedMandateData?.mandateState || mandateState;
            const selectedDealStatus = selectedMandateData?.dealStatus || dealStatus;
            const data = await fetchMandateQualifications(mandateId);
            if (data?.result === true && selectedMandateState === MANDATE_STATES.DRAFT && selectedDealStatus === DealStatus.DRAFT) {
                dispatch(
                    updateDeal({
                        dealId: mandateId,
                        dealStatus: DealStatus.REVIEW_BASE_DATA,
                        lastUpdatedBy: getPlatformUserId(),
                        userId: user.userData.userId,
                    }),
                );
                setDealStatus(DealStatus.REVIEW_BASE_DATA);
            }
        } catch (error) {
            console.error('Error fetching mandate qualifications:', error);
        }
    };

    function buildBasePayload({ dealId = '', dealStatus = '' }) {
        return {
            ...(dealId && { dealId }),
            ...(dealStatus && { dealStatus }),
            userId: platformUserId,

            owner: {
                name: loggedInUser?.userName,
                userId: platformUserId || getPlatformUserId(),
                activityBy: loggedInUser?.userName,
                activityDate: new Date().toISOString(),
                ownerType: DEAL_CREATOR_TYPE,
            },
        };
    }

    const getCustomFieldsForPayload = () => {
        if (!isConfirmationModalVisible) return { partnerOrgId: user.userData.partnerOrgId };
        return null;
    };

    function buildCreatePayload(base) {
        const payload: any = {
            ...base,
            detailedDealTerms: productTerms,
            dealReceiverDetailMap: lenderTerms,
            dealStatus: DealStatus.DRAFT,

            dealProposerId: platformData?.investorOrgId,
            dealBeneficiaryId: params.id,
            dealCreatedBy: loggedInUser?.userName,
        };

        // Add customFields based on user type
        const customFields = getCustomFieldsForPayload();
        if (customFields) payload.customFields = customFields;
        return payload;
    }

    function buildUpdateBaseTermsPayload(base) {
        // Filter to keep only products where isShared === 0 (i.e. not shared)
        const filteredLenderTerms: DealReceiverDetailMap = {};
        Object.keys(lenderTerms).forEach((lenderId) => {
            const filteredProducts: Record<string, ProductDealTerms> = {};
            Object.keys(lenderTerms[lenderId]).forEach((productId) => {
                if (lenderTerms[lenderId][productId].isShared === 0) {
                    filteredProducts[productId] = lenderTerms[lenderId][productId];
                }
            });
            if (Object.keys(filteredProducts).length > 0) {
                filteredLenderTerms[lenderId] = filteredProducts;
            }
        });

        // Only send productTerms if they have changed from original
        const hasProductTermsChanged = !deepCompare(productTerms, originalProductTermsRef.current);

        const payload: any = {
            ...base,
            detailedDealTerms: hasProductTermsChanged ? productTerms : {},
            dealReceiverDetailMap: filteredLenderTerms,
        };

        // Add customFields based on user type
        const customFields = getCustomFieldsForPayload();
        if (customFields) payload.customFields = customFields;

        return payload;
    }

    function buildUpdateNotesDocsPayload(base, processedNote?: string) {
        // If processedNote is provided (from ShareWithLenderModal), override dealNotes
        // to avoid React state race condition where addDealNote hasn't flushed yet
        const terms =
            processedNote !== undefined
                ? {
                      ...dealTerms,
                      dealNotes: [{ note: processedNote, noteBy: user.userData?.userName, noteDate: Date.now() }],
                  }
                : dealTerms;

        const payload: any = {
            ...base,
            dealTerms: terms,
            selectedUsers: selectedUsers,
        };

        // Add customFields based on user type
        const customFields = getCustomFieldsForPayload();
        if (customFields) payload.customFields = customFields;

        return payload;
    }

    function buildDealPayload(scenario: DealPayloadScenario, processedNote?: string) {
        let base = buildBasePayload({
            dealId,
            dealStatus,
        });
        switch (scenario) {
            case DealPayloadScenario.CREATE:
                return buildCreatePayload(base);

            case DealPayloadScenario.UPDATE_BASE:
                base = buildBasePayload({ dealId });
                return buildUpdateBaseTermsPayload(base);

            case DealPayloadScenario.UPDATE_NOTES_DOCS:
                base = buildBasePayload({ dealId, dealStatus: SHARED_DEAL });
                return buildUpdateNotesDocsPayload(base, processedNote);

            default:
                return base;
        }
    }

    const handleDealOperationSuccess = async (scenario: DealPayloadScenario, newDealId?: string) => {
        const finalDealId = newDealId || dealId;

        if (newDealId) setDealId(newDealId);

        setDealStatus(dealStatus);

        // 1. Refresh dropdown
        await getDealsByStatusData(
            { orgId: params.id as string, status: validMandateDropdownStatuses },
            {
                onSuccess: (data) => {
                    setDraftDealsData(data);

                    if (!data || data.length === 0) return;

                    // Skip setting selectedMandate if there's a pending action (e.g., switching mandates)
                    // The pending action (handleMandateSelection) will set the correct mandate after save completes
                    if (hasPendingAction()) return;

                    // Try to match dealId first
                    const match = data.find((m) => m.id === finalDealId);

                    if (match) {
                        setSelectedMandate(match);
                    } else {
                        // fallback to first
                        setSelectedMandate(data[0]);
                        setDealId(data[0].id);
                    }
                },
            },
        );

        originalProductTermsRef.current = productTerms;
        originalLenderTermsRef.current = lenderTerms;
        originalDebtProductsRef.current = normalizeDebtProductsData(debtProductsData.rows);

        setSavingDraft(false);

        //Show Mandate creationsuccess modal for create mandate
        if (scenario === DealPayloadScenario.CREATE) {
            dispatch(
                handleDealApplicationDrawer({
                    open: false,
                    source: 'CreateMandate',
                    dealId: '',
                    width: '',
                    heading: '',
                    subHeading: '',
                    showSuccessModal: true,
                    profileGenerationStatus: '',
                }),
            );
        }

        if (scenario === DealPayloadScenario.UPDATE_NOTES_DOCS) {
            setIsShareWithLenderModalOpen(false);
            setIsTransitioning(false);
            setShowShareSuccessModal(true);
        }
    };

    const handleDealOperationError = (scenario: DealPayloadScenario) => {
        setSavingDraft(false);

        if (scenario === DealPayloadScenario.UPDATE_NOTES_DOCS) {
            setIsTransitioning(false);
        }

        // Clear selection if error due to multiple users sharing same mandate
        handleMandateSelection(null);

        getDealsByStatusData(
            { orgId: params.id as string, status: validMandateDropdownStatuses },
            {
                onSuccess: (data) => {
                    setDraftDealsData(data);
                },
            },
        );
    };

    const saveSelection = async (scenario: DealPayloadScenario, processedNote?: string): Promise<void> => {
        setSavingDraft(true);
        const payload = buildDealPayload(scenario, processedNote);

        return new Promise((resolve, reject) => {
            if (scenario === DealPayloadScenario.CREATE) {
                dispatch(
                    createDeal(payload, {
                        onSuccess: (newDealId: string) => {
                            handleDealOperationSuccess(scenario, newDealId);
                            resolve();
                        },
                        onError: () => {
                            handleDealOperationError(scenario);
                            reject();
                        },
                    }),
                );
            } else {
                dispatch(
                    updateDeal(payload, {
                        onSuccess: async () => {
                            await handleDealOperationSuccess(scenario);
                            updateDealStatusOnMandateQualification(dealId);
                            resolve();
                        },
                        onError: () => {
                            handleDealOperationError(scenario);
                            reject();
                        },
                    }),
                );
            }
        });
    };

    const fetchPECDataAndDebtProductsData = (payload?: any, mandateId?: string) => {
        setLoading(true);
        fetchAllPecData({
            requestPayload: {
                aggregatorId: user.userData?.investorOrgId,
                investmentPreferenceType: 'PEC',
                ...(isLender && { lenderId: user.userData?.lenderOrgId }),
            },
        })
            .then((PECdata) => {
                if (PECdata?.length) {
                    setNoPECExists(false);
                    dispatch(setDebtProductsFilters(payload));
                    fetchDebtProductsData(payload, mandateId, true);
                } else {
                    setNoPECExists(true);
                    setLoading(false);
                }
            })
            .catch(() => {
                setLoading(false);
            });
    };

    const fetchDebtProductsData = (payload?: any, mandateId?: string, updateOriginalRefs: boolean = false) => {
        getDebtProductsData({
            orgId: params.id as string,
            aggregatorId: user.userData.investorOrgId,
            mandateId: mandateId !== undefined ? mandateId : dealId,
            ...(payload ? payload : isEmpty(debtProductsFilters) ? {} : { filters: debtProductsFilters }),
        })
            .then((data) => {
                const debtProductsWithLenders = data?.filter((rowdata) => !!rowdata?.lenders?.length) || [];
                const debtProductsWithoutLenders = data?.filter((rowdata) => !rowdata?.lenders?.length) || [];
                const sortedData = [...debtProductsWithLenders, ...debtProductsWithoutLenders];

                // auto open edit profile drawer
                if (isCompanySide && data.length === 0 && showEmptyScreen) {
                    dispatch(
                        setDealDrawer({
                            open: true,
                            drawerLabel: 'Edit Profile',
                            companyName: selectedCompany?.orgName,
                        }),
                    );
                }

                if (updateOriginalRefs) originalDebtProductsRef.current = normalizeDebtProductsData(sortedData);

                dispatch(getDebtProductsDataAction(GET_SOURCES.GET_ROWS, sortedData));
                setLoading(false);
            })
            .catch((err) => {
                console.error('Error in fetching Debt Products Data', err);
                setLoading(false);
            });
    };

    const registerMixpanelForRowExpand = (rowData) => {
        Mixpanel.track(EXPAND_PRODUCT, {
            user_email: user.userData.primaryEmail,
            timestamp: getTimeStamp(),
            productId: rowData?.productId,
            company_id: selectedCompany?.assesseeOrgId,
            company_name: selectedCompany?.orgName,
            pno_id: user.userData.investorOrgId,
            pno_name: user.domainMetadata?.orgName,
        });
    };

    const registerMixpanelForUserHelpClick = (rowData, isParentRow = false) => {
        Mixpanel.track(CLICK_PROBABILITY_HELP, {
            user_email: user.userData.primaryEmail,
            user_name: user?.userData?.userName,
            current_url: window.location.href,
            user_role: user?.userData?.accountType,
            lender_product_id: rowData?.lenderId + '_' + rowData?.productId,
            org_id: selectedCompany?.assesseeOrgId,
            timestamp: getTimeStamp(),
            productId: rowData?.productId,
            company_id: selectedCompany?.assesseeOrgId,
            company_name: selectedCompany?.orgName,
            pno_id: user.userData.investorOrgId,
            pno_name: user.domainMetadata?.orgName,
            ...(!isParentRow && {
                lender_id: rowData?.lenderId,
                lender_name: rowData?.lenderName,
            }),
        });
    };
    const registerMixpanelForEditProfileClicked = (source) => {
        Mixpanel.track(OPEN_EDIT_PROFILE, {
            user_name: user?.userData?.userName,
            current_url: window.location.href,
            user_role: user?.userData?.accountType,
            user_email: user.userData.primaryEmail,
            org_id: selectedCompany?.assesseeOrgId,
            timestamp: getTimeStamp(),
            source: source,
            company_id: selectedCompany?.assesseeOrgId,
            company_name: selectedCompany?.orgName,
            pno_id: user.userData.investorOrgId,
            pno_name: user.domainMetadata?.orgName,
        });
    };
    const registerMixpanelForUpdateMandate = () => {
        Mixpanel.track(AICA_UPDATE_MANDATE, {
            user_name: user?.userData?.userName,
            user_email: user?.userData?.primaryEmail,
            current_url: window.location.href,
            company_name: selectedCompany?.orgName,
            user_role: user?.userData?.accountType,
            mandate_id: dealId,
            org_id: selectedCompany?.assesseeOrgId,
        });
    };
    const registerMixPanelForFiltersOnDebtOptions = () => {
        Mixpanel.track(AICA_FILTERS_ON_DEBT_OPTIONS, {
            user_name: user?.userData?.userName,
            user_email: user?.userData?.primaryEmail,
            current_url: window.location.href,
            company_name: selectedCompany?.orgName,
            user_role: user?.userData?.accountType,
            mandate_id: dealId,
            org_id: selectedCompany?.assesseeOrgId,
        });
    };

    const registerMixpanelForEditTerms = (fieldName: string, rowData: DebtProductRowData, isParentRow: boolean) => {
        Mixpanel.track(AICA_EDIT_TERMS, {
            user_name: user?.userData?.userName,
            user_email: user?.userData?.primaryEmail,
            current_url: window.location.href,
            company_name: selectedCompany?.orgName,
            user_role: user?.userData?.accountType,
            lender_product_id: rowData?.lenderId + '_' + rowData?.productId,
            org_id: selectedCompany?.assesseeOrgId,
            term_edited: fieldName,
        });
    };

    const renderQualificationStatusPill = () => {
        const isQualified = mandateQualificationChecksData?.result !== false;
        return (
            <div style={pillStyles}>
                <span>{isQualified === false ? QUALIFICATION_STATUS.UNQUALIFIED : QUALIFICATION_STATUS.QUALIFIED}</span>
                <img
                    src={isQualified === false ? infoIconRed : infoIconGreen}
                    alt={isQualified === false ? QUALIFICATION_STATUS.UNQUALIFIED : QUALIFICATION_STATUS.QUALIFIED}
                    style={{ height: '0.875rem', width: '0.875rem' }}
                />
            </div>
        );
    };

    /*
      All conditions for disabling the deal action button:
      QE          QC           hasUnshared     Disabled
      true        true         true            false    
      true        true         false           true     
      true        false        false           true     
      false       false        false           true    
      false       false        true            false    
      false       true         true            false   
      false       true         false           true     
      true        false        true            true   
    */

    const isDealActionDisabled = (isShareDeal: boolean): boolean => {
        if (calculatingRowIds.size > 0) return true;

        if (isShareDeal) {
            if (dealShareVisibility.hidden || dealShareVisibility.disabled) return true;
        } else {
            if (createDealVisibility.hidden || createDealVisibility.disabled) return true;
        }

        if (!isShareDeal) return false;

        const hasUnsharedProducts = Object.values(lenderTerms).some((products) =>
            Object.values(products).some((product) => product.isShared === 0),
        );

        if (!hasUnsharedProducts) return true;

        const isQEEnabled = qualificationChecksVisibility.show;

        if (!isQEEnabled) return false;

        return mandateQualificationChecksData?.result === false;
    };

    const renderDealActionButton = () => {
        const isShareDeal = dealId && !hasUnsavedChanges();

        let buttonText = '';
        if (isShareDeal) {
            buttonText = ButtonText.SHARE_MANDATE;
            if (dealShareVisibility.hidden) return null;
        } else if (dealId) {
            buttonText = ButtonText.UPDATE_MANDATE;
        } else {
            buttonText = ButtonText.CREATE_MANDATE;
            if (createDealVisibility.hidden) return null;
        }

        const handleClick = () => {
            if (isShareDeal) {
                if (!isConfirmationModalVisible) {
                    setIsMandateSharingDrawerOpen(true);
                } else {
                    dispatch(
                        setConfigTableModal({
                            show: true,
                            data: {
                                entity: ENTITY_TYPES.MANDATE,
                                actionType: 'shareDeal',
                                source: [ENTITY_TYPES.LEAD, ENTITY_TYPES.MANDATE],
                                dealId: dealId,
                                orgId: params.id,
                                mandateId: dealId,
                                onSuccess: () => {
                                    setIsMandateSharingDrawerOpen(true);
                                },
                            },
                        }),
                    );
                }
                Mixpanel.track(AICA_SHARE_DEAL, {
                    userRole: user?.userData?.accountType,
                    pnoPlatform: getSubdomain(),
                    pnoId: user?.userData?.investorOrgId,
                    userId: user?.userData?.userId,
                    currentUrl: window.location.href,
                    organizationId: params.id,
                });
            } else if (!dealId && mandateModalVisibility.show) {
                if (!isConfirmationModalVisible) {
                    saveSelection(DealPayloadScenario.CREATE);
                } else {
                    dispatch(
                        setConfigTableModal({
                            show: true,
                            data: {
                                entity: ENTITY_TYPES.MANDATE,
                                actionType: 'createMandate',
                                source: [ENTITY_TYPES.LEAD, ENTITY_TYPES.MANDATE],
                                dealId: dealId,
                                orgId: params.id,
                                onSuccess: () => {
                                    saveSelection(DealPayloadScenario.CREATE);
                                },
                            },
                        }),
                    );
                }
            } else {
                registerMixpanelForUpdateMandate();
                saveSelection(DealPayloadScenario.UPDATE_BASE);
            }
        };

        return (
            <div style={{ display: 'flex', alignItems: 'center', gap: '1rem', position: 'relative' }}>
                {isShareDeal &&
                    (mandateQualificationChecksData?.rules?.length > 0 ? (
                        <>
                            <div style={{ position: 'relative', zIndex: 10001 }}>
                                <Tooltip
                                    content={tooltipContent}
                                    direction="left"
                                    fitContent={true}
                                    style={{
                                        position: 'absolute',
                                        bottom: '100%',
                                        top: 'auto',
                                        left: '-60px',
                                        right: 'auto',
                                        transform: 'translateX(-50%)',
                                        marginBottom: '0.5rem',
                                        marginLeft: '0',
                                        zIndex: 99999,
                                        maxWidth: '400px',
                                        minWidth: '300px',
                                    }}
                                    contentCss={{
                                        borderRadius: '8px',
                                        position: 'relative',
                                        zIndex: 99999,
                                        padding: '1rem',
                                        boxShadow: '0px 0px 10px 2px #0000001A',
                                    }}
                                    hideDelay={400}
                                >
                                    {renderQualificationStatusPill()}
                                </Tooltip>
                            </div>
                            <div
                                style={{
                                    width: '1px',
                                    height: '2rem',
                                    backgroundColor: '#E7e7e7',
                                }}
                            />
                        </>
                    ) : (
                        <>
                            {renderQualificationStatusPill()}
                            <div
                                style={{
                                    width: '2px',
                                    height: '2rem',
                                    backgroundColor: '#E7e7e7',
                                    marginLeft: '1rem',
                                }}
                            />
                        </>
                    ))}
                <Button
                    text={buttonText}
                    disabled={isDealActionDisabled(Boolean(isShareDeal))}
                    onClick={handleClick}
                    variant="primary"
                    icon={!isShareDeal ? plusWhite : ShareIcon}
                    iconStyles={{
                        height: '1rem',
                        width: '1rem',
                        margin: '0 0.5rem 0 0',
                    }}
                    style={{
                        marginTop: '0',
                        width: isShareDeal ? '9rem' : '10rem',
                    }}
                />
            </div>
        );
    };

    const renderClearOrDeleteButton = (dealId: string) => {
        if (!dealId || !hasUnsavedChanges()) return null;
        const buttonText = ButtonText.DISCARD_CHANGES;
        const handleClick = () => {
            setPreviousDealData();
        };
        return (
            <Button
                text={buttonText}
                onClick={handleClick}
                variant="secondary"
                iconStyles={{
                    height: '1rem',
                    width: '1rem',
                    margin: '0 0.5rem 0 0',
                }}
                style={{ marginTop: '0', width: '9rem' }}
            />
        );
    };

    /**
     * Determines checkbox checked and disabled state based on productTerms and lenderTerms
     * @param rowData - The row data for the checkbox
     * @param isParentRow - Whether this is a parent row (product) or child row (lender)
     * @returns An object with checked (boolean) and disabled (boolean) properties
     */
    const getCheckboxState = (rowData: DebtProductRowData, isParentRow: boolean): { checked: boolean; disabled: boolean } => {
        const term = isParentRow ? productTerms[rowData.productId] : rowData.lenderId && lenderTerms[rowData.lenderId]?.[rowData.productId];

        // If term doesn't exist → unchecked & enabled
        if (!term) {
            return { checked: false, disabled: false };
        }

        return {
            checked: true,
            disabled: !!term.isShared, // disabled when isShared is truthy (non-zero)
        };
    };

    /**
     * Counts the number of selected lenders for a given product row
     * @param rowData - The product row data containing lenders array
     * @returns The count of selected lenders for this product
     */
    const countSelectedLenderCount = (rowData: DebtProductRowData): number => {
        let count = 0;
        rowData?.lenders?.forEach((lender) => {
            if (lender.lenderId && lenderTerms[lender.lenderId]?.[rowData.productId]) {
                count += 1;
            }
        });
        return count;
    };

    /**
     * Build terms payload for security-only update.
     * Only includes amount/roi/tenure when they have a real value (from row or existingTerms).
     * Uses shared toNumber / hasNonZeroValue so we never send 0 for missing data.
     */
    const buildTermsForSecurityUpdate = (
        rowData: { amount?: string | number; roi?: string | number; tenure?: string | number },
        existingTerms?: ProductDealTerms,
        securityOffered: SecurityOfferedItem[] = [],
    ): ProductTermsPayload => {
        const maximumDealAmount = toNumber(rowData?.amount) ?? existingTerms?.maximumDealAmount;
        const maximumRoi = toNumber(rowData?.roi) ?? existingTerms?.maximumRoi;
        const minimumTenure = toNumber(rowData?.tenure) ?? existingTerms?.minimumTenure;
        return {
            ...(hasNonZeroValue(maximumDealAmount) && { maximumDealAmount }),
            ...(hasNonZeroValue(maximumRoi) && { maximumRoi }),
            ...(hasNonZeroValue(minimumTenure) && { minimumTenure }),
            security: securityOffered,
        };
    };

    /**
     * Build product terms payload for API call
     * Maps the field being edited to the API payload format
     */
    const buildPayloadForEntity = (
        entityId: string,
        productId: string,
        field: EditableFieldConfig['field'],
        value: number,
        existingTerms?: ProductDealTerms,
    ): ProductTermsPayload => {
        const currentProduct = productsById[productId];
        const currentLender = lendersById[`${entityId}_${productId}`];
        const rowData = currentLender || currentProduct;

        // Get values from rowData or fallback to existingTerms
        const maximumDealAmount = toNumber(rowData?.amount) ?? existingTerms?.maximumDealAmount;
        const maximumRoi = toNumber(rowData?.roi) ?? existingTerms?.maximumRoi;
        const minimumTenure = toNumber(rowData?.tenure) ?? existingTerms?.minimumTenure;
        const security = rowData?.security?.length ? rowData.security : existingTerms?.security;

        // Build payload with only defined values
        const payload: ProductTermsPayload = {
            ...(maximumDealAmount !== undefined && { maximumDealAmount }),
            ...(maximumRoi !== undefined && { maximumRoi }),
            ...(minimumTenure !== undefined && { minimumTenure }),
            ...(security?.length && { security }),
        };

        // Override only the edited field
        switch (field) {
            case PRODUCT_TERMS_KEYS.AMOUNT:
                payload.maximumDealAmount = value;
                break;
            case PRODUCT_TERMS_KEYS.ROI:
                payload.maximumRoi = value;
                break;
            case PRODUCT_TERMS_KEYS.TENURE:
                payload.minimumTenure = value;
                break;
        }

        return payload;
    };

    const dispatchProductMetrics = (
        payload: ProductTermsApiPayload,
        calculatingKeys: string[],
        entityType: EntityType,
        useShimmer: boolean = false,
        onSuccess?: () => void,
        onError?: () => void,
        reorderLendersOnSuccess: boolean = false,
    ) => {
        // Add keys to state; flushSync for shimmer so lender rows show shimmer immediately when product is edited
        if (useShimmer) flushSync(() => addToShimmeringState(calculatingKeys));
        else addToCalculatingState(calculatingKeys);

        dispatch(
            generateProductMetrics(
                { aggregatorId: user.userData?.investorOrgId, orgId: params.id as string, productTerms: payload },
                {
                    onSuccess: (data) => {
                        if (useShimmer) removeFromShimmeringState(data, user.userData?.investorOrgId);
                        else removeFromCalculatingState(data, user.userData?.investorOrgId);

                        if (entityType === EntityType.PRODUCT) {
                            setProductTerms((prev) => {
                                const newTerms = { ...prev };
                                const productTermsPayload = payload[user.userData?.investorOrgId] || {};
                                Object.keys(productTermsPayload).forEach((pId) => {
                                    if (newTerms[pId] && productTermsPayload[pId]?.terms) {
                                        newTerms[pId] = {
                                            ...newTerms[pId],
                                            ...productTermsPayload[pId].terms,
                                        };
                                    }
                                });
                                return newTerms;
                            });
                            onSuccess?.();
                            return;
                        }

                        setLenderTerms((prev) => {
                            const next = { ...prev };
                            Object.keys(payload).forEach((lenderId) => {
                                Object.keys(payload[lenderId]).forEach((productId) => {
                                    const entry = payload[lenderId][productId];
                                    const terms = entry?.terms;
                                    if (next[lenderId]?.[productId] && terms) {
                                        next[lenderId] = {
                                            ...next[lenderId],
                                            [productId]: {
                                                ...next[lenderId][productId],
                                                ...terms,
                                                ...(entry.isShared !== undefined && { isShared: entry.isShared }),
                                            },
                                        };
                                    }
                                });
                            });
                            return next;
                        });

                        // Reorder lenders only when editing at PNo (product) level and calling API for all lenders

                        if (reorderLendersOnSuccess) {
                            const lenderResults = Array.isArray(data) ? data : [];
                            if (lenderResults.length) {
                                const byProduct = new Map<string, string[]>();
                                lenderResults.forEach((r: any) => {
                                    const pid = r.productId;
                                    if (!pid) return;
                                    if (!byProduct.has(pid)) byProduct.set(pid, []);
                                    if (r.lenderId) byProduct.get(pid)!.push(r.lenderId);
                                });
                                byProduct.forEach((lenderIdsInOrder, productId) => {
                                    dispatch(reorderProductLenders(productId, lenderIdsInOrder));
                                });
                            }
                        }

                        onSuccess?.();
                    },
                    onError: () => {
                        if (useShimmer) clearShimmeringRows(calculatingKeys);
                        else clearCalculatingRows(calculatingKeys);
                        onError?.();
                    },
                },
            ),
        );
    };

    /**
     * Auto-selects product/lender when terms are edited (if not already selected)
     */
    const autoSelectProductOrLender = (productId: string, lenderId?: string) => {
        if (!productTerms[productId]) {
            const productRow = findProductRow(debtProductsData.rows, productId);
            if (productRow) {
                setProductTerms((prev) => ({ ...prev, [productId]: createDealTerms(productRow) }));
            }
        }

        if (lenderId && !lenderTerms[lenderId]?.[productId]) {
            const lenderRow = findLenderRow(debtProductsData.rows, productId, lenderId);
            if (lenderRow) {
                setLenderTerms((prev) => ({
                    ...prev,
                    [lenderId]: { ...(prev[lenderId] || {}), [productId]: createDealTerms(lenderRow) },
                }));
            }
        }
    };

    /**
     * Update editable field (amount, tenure, roi) for a product or lender row
     * For product edits: dispatches two separate actions (product and lenders) in parallel
     * For lender edits: dispatches single action for that lender only
     * Updates Redux via saga and local state on success
     * Auto-selects the product/lender if not already selected
     */
    const updateEditableField = (
        field: EditableFieldConfig['field'],
        newValue: any,
        rowData: DebtProductRowData,
        isParentRow: boolean,
        handlers?: { onSuccess?: () => void; onError?: () => void },
    ) => {
        const numericValue = Number(newValue) || 0;
        const investorOrgId = user.userData?.investorOrgId;
        const orgId = params.id as string;
        if (!investorOrgId || !orgId) return handlers?.onError?.();

        autoSelectProductOrLender(rowData.productId, isParentRow ? undefined : rowData.lenderId);

        if (isParentRow) {
            // Product row
            const productTermsData = buildPayloadForEntity(
                investorOrgId,
                rowData.productId,
                field,
                numericValue,
                productTerms[rowData.productId],
            );
            const productPayload: ProductTermsApiPayload = {
                [investorOrgId]: {
                    [rowData.productId]: { terms: productTermsData },
                },
            };
            const productKey = getCalculatingKey(rowData.productId, investorOrgId, EntityType.PRODUCT);

            const lenderPayload: ProductTermsApiPayload = {};
            const lenderKeys: string[] = [];
            productsById[rowData.productId]?.lenderIds?.forEach((lenderKey) => {
                const lender = lendersById[lenderKey];
                if (!lender?.lenderId) return;
                const isShared = lenderTerms[lender.lenderId]?.[rowData.productId]?.isShared;

                //Skip the lenders that are shared
                if (isShared === 1 || isShared === -1) return;

                lenderPayload[lender.lenderId] = {
                    [rowData.productId]: {
                        terms: buildPayloadForEntity(
                            lender.lenderId,
                            rowData.productId,
                            field,
                            numericValue,
                            lenderTerms[lender.lenderId]?.[rowData.productId],
                        ),
                        ...(isShared === 0 && { isShared: 0 }),
                    },
                };
                lenderKeys.push(getCalculatingKey(lender.lenderId, rowData.productId, EntityType.LENDER));
            });

            dispatchProductMetrics(productPayload, [productKey], EntityType.PRODUCT, false, handlers?.onSuccess, handlers?.onError);
            // Use shimmer effect for lenders when editing product level
            if (Object.keys(lenderPayload).length)
                dispatchProductMetrics(lenderPayload, lenderKeys, EntityType.LENDER, true, handlers?.onSuccess, handlers?.onError, true);
        } else if (rowData.lenderId) {
            // Lender row
            const payload: ProductTermsApiPayload = {
                [rowData.lenderId]: {
                    [rowData.productId]: {
                        terms: buildPayloadForEntity(
                            rowData.lenderId,
                            rowData.productId,
                            field,
                            numericValue,
                            lenderTerms[rowData.lenderId]?.[rowData.productId],
                        ),
                    },
                },
            };
            const calculatingKey = getCalculatingKey(rowData.lenderId, rowData.productId, EntityType.LENDER);
            dispatchProductMetrics(payload, [calculatingKey], EntityType.LENDER, false, handlers?.onSuccess, handlers?.onError, false);
        }
    };

    /**
     * Update security offered for a product/lender or all selected products and lenders
     * Called from SecurityModal when user saves security selection
     * Auto-selects the product/lender if not already selected
     */
    // single function, no nested functions
    const updateSecurityOffered = (data: {
        productId: string;
        lenderId?: string;
        isParentRow: boolean;
        securityOffered: SecurityOfferedItem[];
        copyToAll: boolean;
    }) => {
        const { productId, lenderId, isParentRow, securityOffered, copyToAll } = data;
        const investorOrgId = user.userData?.investorOrgId;
        const orgId = params.id as string;
        if (!investorOrgId || !orgId) return;

        autoSelectProductOrLender(productId, isParentRow ? undefined : lenderId);

        const productPayload: ProductTermsApiPayload = { [investorOrgId]: {} };
        const lenderPayload: ProductTermsApiPayload = {};
        const productKeys: string[] = [];
        const lenderKeys: string[] = [];
        let mandateId: string | undefined = undefined;

        if (isParentRow) {
            // Product row: update product and all lenders for that product
            // When copyToAll: include all products from productTerms + current productId (deduplicated)
            let allProducts = copyToAll ? [...new Set([...Object.keys(productTerms), productId])] : [productId];
            // Filter out products that are present in productTerms and do not have isShared === 0 or 2
            allProducts = allProducts.filter((pId) => {
                const existingTerms = productTerms[pId];
                // Keep product if it's not in productTerms, or if it is in productTerms and has isShared === 0 or 2
                return !existingTerms || existingTerms.isShared === 0 || existingTerms.isShared === 2;
            });
            allProducts.forEach((pId) => {
                const product = productsById[pId];
                if (!product) return;

                const existingTerms = productTerms[pId];

                productPayload[investorOrgId][pId] = {
                    terms: buildTermsForSecurityUpdate(product, existingTerms, securityOffered),
                };
                productKeys.push(getCalculatingKey(pId, investorOrgId, EntityType.PRODUCT));

                // Lenders - process all lenders for this product
                product?.lenderIds?.forEach((lenderKey) => {
                    const lender = lendersById[lenderKey];
                    if (!lender?.lenderId) return;
                    const isShared = lenderTerms[lender.lenderId]?.[pId]?.isShared;

                    //Skip the lenders that are shared
                    if (isShared === 1 || isShared === -1) return;

                    if (!lenderPayload[lender.lenderId]) lenderPayload[lender.lenderId] = {};

                    lenderPayload[lender.lenderId][pId] = {
                        terms: buildTermsForSecurityUpdate(lender, lenderTerms[lender.lenderId]?.[pId], securityOffered),
                        ...(isShared === 0 && { isShared: 0 }),
                    };
                    lenderKeys.push(getCalculatingKey(lender.lenderId, pId, EntityType.LENDER));
                    mandateId = dealId;
                });
            });
        } else if (lenderId) {
            // Lender row: update only this specific lender
            const lender = lendersById[`${lenderId}_${productId}`];
            if (!lender?.lenderId) return;

            const isShared = lenderTerms[lenderId]?.[productId]?.isShared;
            //Skip the lender if it is shared
            if (isShared === 1 || isShared === -1) return;

            lenderPayload[lenderId] = {
                [productId]: {
                    terms: buildTermsForSecurityUpdate(lender, lenderTerms[lenderId]?.[productId], securityOffered),
                },
            };
            lenderKeys.push(getCalculatingKey(lenderId, productId, EntityType.LENDER));
        }

        // Dispatch
        if (productKeys.length > 0) dispatchProductMetrics(productPayload, productKeys, EntityType.PRODUCT, false);
        if (Object.keys(lenderPayload).length)
            dispatchProductMetrics(lenderPayload, lenderKeys, EntityType.LENDER, isParentRow, undefined, undefined, isParentRow);
    };
    const activeDataSource = companyMetricsData?.borrowerFinancials?.activeSource;
    const activeCompletionPercentage = useMemo(() => {
        if (!percentageCompletion?.borrowerFinancials) return undefined;

        return activeDataSource === DATA_SOURCE_TYPE.DISCUSSION
            ? percentageCompletion.borrowerFinancials.discussion
            : percentageCompletion.borrowerFinancials.actualData;
    }, [activeDataSource, percentageCompletion]);

    const handleLastDealSharedClick = useCallback(
        (rowData: DebtProductRowData, isParentRow: boolean, e: React.MouseEvent) => {
            e.stopPropagation();
            if (disableLastDealSharedClick) return;
            const category = isParentRow ? DEAL_VIEW_TYPES.MANDATE_VIEW : DEAL_VIEW_TYPES.DEAL_VIEW;
            const basePath = `${window.location.origin}/investor/app/company-deals/${params.id}`;
            const searchParams = new URLSearchParams({
                page: '1',
                fetchType: 'all',
                type: 'deals',
                category,
                tab: 'deals',
                [FROM_DEBT_OPTIONS_PARAM]: '1',
            });
            if (isParentRow && rowData?.productId) {
                searchParams.set('loanType', rowData.productId);
            } else if (!isParentRow && rowData?.lenderId) {
                searchParams.set('lenderId', rowData.lenderId);
            } else {
                return;
            }
            const dealsUrl = `${basePath}?${searchParams.toString()}`;
            window.open(dealsUrl, '_blank', 'noopener,noreferrer');
        },
        [params.id],
    );

    return {
        loading,
        noPecExists,
        companyMetricsDataExists,
        companyMetricsData,
        metricsMetadata,
        showDetailOverview,
        setShowDetailOverview,
        debtProductsData,
        showEmptyScreen,
        dealDrawerState,
        fetchPECDataAndDebtProductsData,
        fetchCompanyMetrics,
        updateProductTerms,
        updateLenderTerms,
        dealPayload,
        saveSelection,
        savingDraft,
        fetchDebtProductsData,
        dealId,
        //Mixpanel
        registerMixpanelForRowExpand,
        registerMixpanelForUserHelpClick,
        registerMixpanelForEditProfileClicked,
        showFilters,
        renderDealActionButton,
        renderClearOrDeleteButton,
        dealStatus,
        originalDealPayloadRef,
        draftDealsData,
        dealStatusConfig,
        selectedMandate,
        handleMandateSelection,
        mandateState,
        percentageCompletion,
        getCheckboxState,
        countSelectedLenderCount,
        aggregatedMandateTerms,
        isMandateSharingDrawerOpen,
        setIsMandateSharingDrawerOpen,
        isShareWithLenderModalOpen,
        setIsShareWithLenderModalOpen,
        productTerms,
        lenderTerms,
        dealTerms,
        updateEndUseOfCapital,
        addDealNote,
        updateDealDocuments,
        selectedUsers,
        updateSelectedUsers,
        updateEditableField,
        isRowCalculating,
        isRowShimmering,
        // UI transition states
        isTransitioning,
        setIsTransitioning,
        // Share success modal state
        showShareSuccessModal,
        setShowShareSuccessModal,
        // Security modal state
        showSecurityModal,
        setShowSecurityModal,
        updateSecurityOffered,
        // Probability tooltip
        getProbabilityTooltip,
        // Unsaved changes modal
        showUnsavedChangesModal,
        handleActionWithUnsavedCheck,
        handleCloseWithoutSaving,
        handleSaveChanges,
        handleCloseUnsavedModal,
        registerMixPanelForFiltersOnDebtOptions,
        registerMixpanelForEditTerms,
        activeCompletionPercentage,
        // Row hover state for editable fields
        setEditableRowHover,
        isEditableRowHovered,
        getCalculatingRowKey,
        // Audited financials banner and row click state
        isBannerDismissed,
        setIsBannerDismissed,
        hasUserClickedRow,
        setHasUserClickedRow,
        showAuditedBanner,
        handleLastDealSharedClick,
        createDealVisibility,
        dealShareVisibility,
        editProfileVisibility,
        accordionVisibility,
        selectProductVisibility,
        selectLenderVisibility,
        getFeatureVisibility,
    };
};

export default useDebtProductLogic;
